/**
 * TaxYearInput — validated tax-year number input using centralized rules.
 *
 * Renders inline RED error for impossible values, AMBER confirm for stale years.
 * Bounds derive from new Date() — nothing hardcoded.
 *
 * Props:
 *   value      — numeric year
 *   onChange(n) — called with the number
 *   testid     — data-testid
 *   className  — extra class on outer wrapper
 *   disabled   — disables input
 *   onValidation — optional ({ error, warning }) callback for parent save-block
 */
import React, { useState, useCallback, useEffect } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { validateTaxYear } from "@/lib/dateValidation";

export function TaxYearInput({ value, onChange, testid, className = "", disabled = false, onValidation }) {
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [warningAcked, setWarningAcked] = useState(false);

  const validate = useCallback((v) => {
    const n = Number(v);
    if (!v && v !== 0) { setError(""); setWarning(""); setWarningAcked(false); onValidation?.({ error: null, warning: null }); return; }
    const r = validateTaxYear(n);
    setError(r.error || "");
    setWarning(r.warning || "");
    if (r.warning !== warning) setWarningAcked(false);
    onValidation?.(r);
  }, [onValidation, warning]);

  useEffect(() => { validate(value); }, [value]); // eslint-disable-line

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === "") { onChange(""); return; }
    const n = Number(raw);
    if (Number.isFinite(n)) onChange(n);
  };

  return (
    <div className={className}>
      <input
        type="number"
        min="1996"
        step="1"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        data-testid={testid}
        className={`h-9 w-24 rounded-md border px-2 text-sm ${error ? "border-[#F01919] ring-1 ring-[#F01919]/30" : warning && !warningAcked ? "border-[#D97706] ring-1 ring-[#D97706]/30" : "border-[#C2C9D1]"} ${disabled ? "opacity-50" : ""}`}
      />
      {error && (
        <p className="mt-0.5 text-[10px] text-[#F01919] font-medium leading-tight" data-testid={testid ? `${testid}-error` : undefined}>{error}</p>
      )}
      {!error && warning && !warningAcked && (
        <div className="mt-0.5 flex items-start gap-1 rounded border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1" data-testid={testid ? `${testid}-warning` : undefined}>
          <WarningCircle size={13} weight="fill" className="mt-px shrink-0 text-[#D97706]" />
          <span className="flex-1 text-[10px] text-[#92400E] leading-tight">{warning}</span>
          <button type="button" onClick={() => setWarningAcked(true)} className="shrink-0 text-[10px] font-bold text-[#92400E] underline hover:no-underline" data-testid={testid ? `${testid}-ack` : undefined}>OK</button>
        </div>
      )}
    </div>
  );
}
